import {
  Button,
  ButtonLoading,
  CaretRightIcon,
  ThumbsUpIcon,
} from '@tovala/component-library'
import { clsx } from 'clsx'
import { uniqBy } from 'lodash-es'
import {
  ComponentProps,
  Fragment,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Listing,
  ListingsReceipt,
  OrderHistoryReceipt,
  OrderHistoryReceiptMeal,
  ReviewShort,
  ReviewSummary,
  useListingsReceipt,
  useMealReviewSummaries,
} from '@tovala/browser-apis-combinedapi'
import { useMenuProductDetailsJSON } from '@tovala/browser-apis-cdn'

import { DATE_FORMATS, formatDate, isDatePast, parseToDate } from 'utils/dates'
import { ErrorCodeMessageMapCombinedAPI, ListingFull } from 'types/internal'
import { events } from '../../../analytics/events'
import { getDeliveryStatus } from 'utils/terms'
import { track } from 'utils/analytics'
import { getMealImageURL } from 'utils/meals'

import { useFirstOvenCookHistory } from 'hooks/cookHistory'
import { useOrderHistory } from 'hooks/orderHistory'
import { useUser } from 'contexts/user'
import { useVariantByScreenSize } from 'hooks/variantByScreenSize'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import Link from 'components/common/Link'
import StarIcon from 'components/common/icons/StarIcon'
import ListingDetailsDialog from 'components/common/details/ListingDetailsDialog'
import MealDetailsDialog from 'components/common/details/MealDetailsDialog'
import ProfileHeading from './AccountHeading'
import MealRatingDialog from '../rateMeals/MealRatingDialog'
import RateMealsPrompt from '../rateMeals/RateMealsPrompt'
import { useMostRecentTermMealsToRate } from '../rateMeals/hooks'
import Receipt from './Receipt'
import ReportIssue from './reportIssue/ReportIssue'
import { SmallMealImage } from 'components/common/MealImage'

type ButtonSize = ComponentProps<typeof Button>['size']
type LinkSize = ComponentProps<typeof Link>['size']

const starsRatings = [
  {
    label: '1 out of 5 Stars',
    rating: 20,
  },
  {
    label: '2 out of 5 Stars',
    rating: 40,
  },
  {
    label: '3 out of 5 Stars',
    rating: 60,
  },
  {
    label: '4 out of 5 Stars',
    rating: 80,
  },
  {
    label: '5 out of 5 Stars',
    rating: 100,
  },
]

const LOAD_ORDER_HISTORY_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please reload the page.',
    why: "We couldn't load your order history due to a technical issue on our end.",
  },
}

const OrderHistoryPage = () => {
  const { user } = useUser()

  const { data: ovenCookHistory = [] } = useFirstOvenCookHistory({
    userID: user.id,
  })

  const {
    getOrderHistoryReceiptsError,
    hasGetOrderHistoryReceiptsError,
    isFetchingNextPage,
    isLoading,
    loadMoreOrderHistory,
    receipts,
    recentOrdersPastDeliveryDate,
    totalOrderCount,
  } = useOrderHistory({ user })

  const { data: mealReviewSummaries } = useMealReviewSummaries({
    userID: user.id,
  })

  const mostRecentTermMealsToRate = useMostRecentTermMealsToRate({
    cookHistory: ovenCookHistory,
    mealReviewSummaries,
    orderHistoryReceipts: receipts,
  })

  const showMoreHistory =
    totalOrderCount !== undefined && receipts.length < totalOrderCount

  const [searchParams, setSearchParams] = useSearchParams()
  const hasAutoOpenedRef = useRef(false)

  useEffect(() => {
    if (hasAutoOpenedRef.current || isLoading || receipts.length === 0) {
      return
    }

    const trackTermParam = searchParams.get('trackTerm')
    if (!trackTermParam) {
      return
    }

    hasAutoOpenedRef.current = true

    const trackTermID = Number(trackTermParam)
    const termReceipt = receipts.find((r) => r.termID === trackTermID)
    if (termReceipt?.trackingURL) {
      window.open(termReceipt.trackingURL, '_blank', 'noopener,noreferrer')
    }

    const params = new URLSearchParams(searchParams)
    params.delete('trackTerm')
    setSearchParams(params, { replace: true })
  }, [isLoading, receipts, searchParams, setSearchParams])

  useEffect(() => {
    track(events.OPENS_ORDER_HISTORY)
  }, [])

  return (
    <div>
      <div className="hidden md:mb-8 md:block">
        <ProfileHeading to="/account">Order History</ProfileHeading>
      </div>
      <div className="flex flex-wrap gap-4">
        <div className="mb-9 max-w-[1040px] grow">
          <h1 className="mb-6 text-k/44_110 md:hidden">Order History</h1>

          <div className="space-y-10 md:space-y-6">
            {receipts.length > 0 ? (
              receipts.map((userTermOrder, index) => {
                const isRecentOrder = recentOrdersPastDeliveryDate
                  .map((receipt) => receipt.termID)
                  .includes(userTermOrder.termID)

                return (
                  <Fragment key={userTermOrder.userTermOrderID}>
                    <HistoryTerm
                      isRecentOrder={isRecentOrder}
                      mealReviewSummaries={mealReviewSummaries}
                      userID={user.id}
                      userTermOrder={userTermOrder}
                    />

                    {index === 0 && (
                      <div className="md:mx-4">
                        <RateMealsPrompt
                          mostRecentTermMealsToRate={mostRecentTermMealsToRate}
                        />
                      </div>
                    )}
                  </Fragment>
                )
              })
            ) : hasGetOrderHistoryReceiptsError ? (
              <APIErrorDisplay
                error={getOrderHistoryReceiptsError}
                errorCodeMessageMap={LOAD_ORDER_HISTORY_ERRORS}
              />
            ) : !isLoading && !totalOrderCount ? (
              <p>No order history found.</p>
            ) : null}

            {showMoreHistory && (
              <div className="md:px-4">
                <ButtonLoading
                  isLoading={isFetchingNextPage}
                  onClick={() => {
                    loadMoreOrderHistory()
                  }}
                  size="large"
                >
                  Show more
                </ButtonLoading>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderHistoryPage

const HistoryTerm = ({
  isRecentOrder,
  mealReviewSummaries,
  userID,
  userTermOrder,
}: {
  isRecentOrder: boolean
  mealReviewSummaries: ReviewSummary | undefined
  userID: number
  userTermOrder: OrderHistoryReceipt
}) => {
  const [showReceipt, setShowReceipt] = useState(false)
  const [selectedMealIDToRate, setSelectedMealIDToRate] = useState<
    number | null
  >(null)

  const [historyButtonSize, historyLinkSize] = useVariantByScreenSize<
    [ButtonSize, LinkSize]
  >(['medium', 'medium'], {
    md: ['small', 'small'],
  })

  const { data: listingsReceipt } = useListingsReceipt({
    orderID: userTermOrder.userTermOrderID,
    userID,
  })

  const deliveryDate = formatDate(userTermOrder.deliveryDate, {
    format: DATE_FORMATS.DOW_MONTH_FULL_DAY,
  })

  const { listings, meals } = getReceiptSelections({
    listingsReceipt,
    receipt: userTermOrder,
  })
  const selectedMealToRate = meals.find(
    (meal) => meal.id === selectedMealIDToRate,
  )

  return (
    <div className="rounded-xl bg-grey-2 md:rounded-none md:bg-grey-0">
      <div className="flex flex-wrap items-center justify-between gap-3 p-6 md:flex-col md:items-start md:bg-grey-2 md:p-4">
        <div className="shrink-0 text-k/20_125">
          {getDeliveryStatus({ receipt: userTermOrder })} {deliveryDate}
        </div>

        <div className="flex items-center space-x-4">
          {(userTermOrder.orderStatus === 'complete' ||
            userTermOrder.orderStatus === 'refunded') && (
            <div>
              {userTermOrder.trackingURL ? (
                <Link
                  linkStyle="white"
                  linkType="external"
                  onClick={() => {
                    track(events.OPENS_ORDER_TRACKING, {
                      term_id: userTermOrder.termID,
                    })
                  }}
                  rel="noreferrer"
                  size={historyLinkSize}
                  target="_blank"
                  to={userTermOrder.trackingURL}
                >
                  Track Order
                </Link>
              ) : (
                // Anchor tags can't be disabled like a button, but leaving off the href and adding role="link" allows us to use aria-disabled
                <a
                  aria-disabled="true"
                  className="flex h-9 items-center justify-center whitespace-nowrap rounded-full bg-grey-7 px-8 text-sm text-grey-2 md:h-7 md:px-6 md:text-xs"
                  role="link"
                >
                  Track Order
                </a>
              )}
            </div>
          )}

          {(userTermOrder.orderStatus === 'complete' ||
            userTermOrder.orderStatus === 'refunded' ||
            userTermOrder.orderStatus === 'canceled') && (
            <>
              <Button
                buttonStyle="white"
                onClick={() => {
                  setShowReceipt(true)
                  track(events.OPENS_ORDER_RECEIPT, {
                    term_id: userTermOrder.termID,
                  })
                }}
                size={historyButtonSize}
              >
                Receipt
              </Button>
            </>
          )}

          {isRecentOrder && <ReportIssue buttonSize={historyLinkSize} />}
        </div>
      </div>

      {(userTermOrder.orderStatus === 'complete' ||
        userTermOrder.orderStatus === 'refunded') && (
        <div className="space-y-6 border-t border-grey-4 p-6 md:space-y-4 md:border-none md:px-4 md:py-6">
          <HistoryItemsGrid>
            {meals.map((selectedMeal) => {
              const { quantity, ...meal } = { ...selectedMeal }

              return (
                <HistoryTermMeal
                  key={meal.id}
                  meal={meal}
                  mealReviewSummaries={mealReviewSummaries}
                  onRateClick={() => {
                    setSelectedMealIDToRate(meal.id)
                  }}
                  quantity={quantity}
                  userTermOrder={userTermOrder}
                />
              )
            })}
          </HistoryItemsGrid>

          {userTermOrder.autofillProcessing && (
            <p className="text-k/16_125">
              Auto-Selection In Progress: Your Selections Will be Finalized By
              Our Chefs on Thursday.
            </p>
          )}

          {listings.length > 0 && (
            <div className="border-t border-grey-4 pt-6 md:pt-4">
              <HistoryItemsGrid>
                {listings.map((selectedListing) => {
                  const { quantity, ...listing } = { ...selectedListing }
                  return (
                    <HistoryTermExtra
                      key={listing.id}
                      listing={listing}
                      quantity={quantity}
                    />
                  )
                })}
              </HistoryItemsGrid>
            </div>
          )}
        </div>
      )}

      {showReceipt && (
        <Receipt
          onCloseModal={() => setShowReceipt(false)}
          selections={{
            listings,
            meals,
          }}
          userTermOrder={userTermOrder}
        />
      )}
      {selectedMealToRate && (
        <MealRatingDialog
          meal={selectedMealToRate}
          onClose={() => setSelectedMealIDToRate(null)}
        />
      )}
    </div>
  )
}

const HistoryTermExtra = ({
  listing,
  quantity,
}: {
  listing: Listing
  quantity: number
}) => {
  const [showModal, setShowModal] = useState(false)

  const { data: listingDetails } = useMenuProductDetailsJSON({
    productDetailsURL: showModal ? listing?.productDetailsURL : undefined,
  })
  const listingFull: ListingFull | undefined =
    listing && listingDetails
      ? {
          ...listingDetails,
          ...listing,
        }
      : undefined

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }

  return (
    <>
      {showModal && listingFull && (
        <ListingDetailsDialog listing={listingFull} onClose={closeModal} />
      )}

      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={openModal}
      >
        <div className="mr-4 flex items-center">
          <div className="relative mr-4 shrink-0">
            <SmallMealImage imageURL={listing.imageURL} />
            {quantity > 1 && (
              <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-sm bg-white text-h/14_120 font-semibold text-orange-1">
                {quantity}
              </span>
            )}
          </div>

          <p className="text-k/16_125 md:text-h/14_120">{listing.title}</p>
        </div>
        <div className="hidden h-6 w-6 shrink-0 text-grey-6 md:block">
          <CaretRightIcon />
        </div>
      </div>
    </>
  )
}

const HistoryItemsGrid = ({ children }: { children: ReactNode }) => {
  return (
    <div className="grid grid-cols-2 gap-6 lg:grid-cols-1 md:gap-4">
      {children}
    </div>
  )
}

const HistoryTermMeal = ({
  meal,
  mealReviewSummaries,
  onRateClick,
  quantity,
  userTermOrder,
}: {
  meal: OrderHistoryReceiptMeal
  mealReviewSummaries: ReviewSummary | undefined
  onRateClick: () => void
  quantity: number
  userTermOrder: OrderHistoryReceipt
}) => {
  const [showModal, setShowModal] = useState(false)

  const rateButtonSize = useVariantByScreenSize<ButtonSize>('medium', {
    md: 'small',
  })

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }

  const isPastDeliveryDate =
    userTermOrder.deliveryDate &&
    isDatePast(parseToDate(userTermOrder.deliveryDate))

  let completedReview: ReviewShort | '' | undefined = ''
  if (mealReviewSummaries && mealReviewSummaries.reviews) {
    completedReview = mealReviewSummaries.reviews.find(
      (review) => review.mealId === meal.id,
    )
  }

  return (
    <>
      {showModal && <MealDetailsDialog closeModal={closeModal} meal={meal} />}

      <div className="mr-4 flex cursor-pointer items-center">
        <div className="relative mr-4 shrink-0" onClick={openModal}>
          <SmallMealImage imageURL={getMealImageURL(meal)} />
          {quantity > 1 && (
            <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-sm bg-white text-h/14_120 font-semibold text-orange-1">
              {quantity}
            </span>
          )}
        </div>

        <div className="space-y-2">
          <div onClick={openModal}>
            <p className="text-k/16_125 md:text-h/14_120">{meal.title}</p>
            <p className="mt-1 text-k/14_120 md:text-k/12_120">
              {meal.subtitle}
            </p>
            <button className="sr-only" onClick={openModal}>
              View meal details
            </button>
          </div>

          {completedReview ? (
            <CompletedReview review={completedReview} />
          ) : (
            <div>
              <Button
                buttonStyle="stroke"
                disabled={!isPastDeliveryDate}
                onClick={onRateClick}
                size={rateButtonSize}
              >
                Rate
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const CompletedReview = ({ review }: { review: ReviewShort }) => {
  if (review === undefined) {
    return null
  }
  return (
    <div className="cursor-default">
      {review.rating === 100 && review.ratingType === 'thumbs' ? (
        <div className="h-6 w-6">
          <ThumbsUpIcon />
          <div className="sr-only">Thumbs up</div>
        </div>
      ) : review.ratingType === 'stars' ? (
        <div className="flex space-x-1">
          {starsRatings.map(({ rating }) => {
            const isFilled = review.rating >= rating
            return (
              <div
                key={rating}
                className={clsx('h-6 w-6', {
                  'text-orange-2': isFilled,
                  'text-[#D9D9D9]': !isFilled,
                })}
              >
                <StarIcon />
              </div>
            )
          })}

          <div className="sr-only">
            {
              starsRatings.find(({ rating }) => {
                return rating === review.rating
              })?.label
            }
          </div>
        </div>
      ) : null}
    </div>
  )
}

function getReceiptSelections({
  listingsReceipt,
  receipt,
}: {
  listingsReceipt: ListingsReceipt | undefined
  receipt: OrderHistoryReceipt
}): {
  listings: (Listing & { quantity: number })[]
  meals: (OrderHistoryReceiptMeal & { quantity: number })[]
} {
  const selectedListings = listingsReceipt?.listings ?? []

  const uniqueSelectedListings = uniqBy(selectedListings, 'id')
  const uniqueSelectedMeals = uniqBy(receipt.selectedMeals, 'id')

  return {
    listings: uniqueSelectedListings.map((listing) => {
      const quantity = selectedListings.filter(
        (l) => l.id === listing.id,
      ).length

      return {
        ...listing,
        quantity,
      }
    }),
    meals: uniqueSelectedMeals.map((meal) => {
      const quantity = receipt.selectedMeals.filter(
        (m) => m.id === meal.id,
      ).length

      return {
        ...meal,
        quantity,
      }
    }),
  }
}
